import { CommonModule } from "@angular/common";
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { DevicesComponent } from "../devices/devices.component";
import { ApiHandlerService } from "../../core/services/apiCall.service";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { SelectedDevicesService } from "../../core/services/device-selection.service";
import { LoadingComponent } from "../../shared/loading/loading.component";
import { DomSanitizer } from "@angular/platform-browser";
import { NotificationStateService } from "../../core/services/notifications.service";
import { ToastrService } from "ngx-toastr";

const MONTHLY = "monthly";
const YEARLY = "yearly";

@Component({
  selector: "app-plan-details",
  standalone: true,
  imports: [CommonModule, DevicesComponent, LoadingComponent],
  templateUrl: "./plan-details.component.html",
  styleUrl: "./plan-details.component.scss",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PlanDetailsComponent implements OnInit {
  @Input() shopCampatibleDevices: boolean = false;
  activeIndex: number = 0;
  newPlanList: any = [];
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  showPriceTag: boolean = false;
  planDetails: any = [];
  selectedDevices: any = [];
  devices: any = [];
  activePlan: any;
  user: any;
  region: any;
  sanitizedCheckoutUrl: any;
  checkoutUrl: any;
  plansBasedOnCycle: any = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private service: ApiHandlerService,
    private route: ActivatedRoute,
    private router: Router,
    private selectedDevicesService: SelectedDevicesService,
    private sanitizer: DomSanitizer,
    private notificationService: NotificationStateService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.isLoading$.next(true);
    this.selectedDevicesService.selectedDevices$.subscribe((devices) => {
      this.selectedDevices = devices;
    });
    if (!this.selectedDevices.length) {
      this.router.navigate(["/home"]);
      this.isLoading$.next(false);
    } else {
      this.getNewPlans();
    }

    if (typeof window !== "undefined" && window.localStorage) {
      this.region = localStorage.getItem("selectedCountry");
      this.user = localStorage.getItem("user");
    }

    this.setActiveTab(MONTHLY);
  }

  currentPrice: string = ""; // Initial price (monthly)
  activeTab: string = MONTHLY; // Track the active tab

  setActiveTab(tabName: string) {
    this.activeIndex = 0;
    this.currentPrice = "";
    this.activeTab = tabName; // Update the active tab
    this.filterPlansOnPricingCycle(tabName);
    this.cdr.detectChanges(); // Ensure change detection runs
  }

  setActive(index: number, plan: any) {
    this.activeIndex = index;
    this.activePlan = plan;
    this.showPriceTag = true;
    let price = 0;
    if (this.activeTab == MONTHLY) {
      price = this.getPriceForRegion(plan, MONTHLY, this.region);
    } else if (this.activeTab == YEARLY) {
      price = Math.round(this.getPriceForRegion(plan, YEARLY, this.region) * 100 ) / 100;
    }
    const channels = this.activePlan?.details?.association?.channels;
    const maxLimitPerSubscription =
      channels >= 1 && channels < 10 ? channels : 10;
    const noOfSubscriptionRequired = Math.ceil(
      this.selectedDevices.length / maxLimitPerSubscription
    );
    this.currentPrice = (price * noOfSubscriptionRequired).toString();
    this.isLoading$.next(false);
  }

  getPriceForRegion(plan: any, cycle: string, region: string) {
    const regionalPriceInfo =
      plan?.availability?.prices?.[region.toUpperCase()];
    if (Array.isArray(regionalPriceInfo)) {
      for (const pi of regionalPriceInfo) {
        if (pi.cycle?.toLowerCase() == cycle) {
          return pi.price;
        }
      }
      return undefined;
    }
    if ("object" === typeof regionalPriceInfo) {
      if (regionalPriceInfo.cycle?.toLowerCase() == cycle) {
        return regionalPriceInfo.price;
      }
      return undefined;
    }
    return undefined;
  }

  exceedCamCap(plan: any) {
    const selectedDevicesCount = this.selectedDevices.length;
    if (selectedDevicesCount > plan.details?.association?.channels) {
      return true;
    }
    return false;
  }

  filterPlansOnPricingCycle(cycle: string) {
    // console.log("Cycle > ", cycle);
    // console.log("Region > ", this.region);
    // console.log("Plan List: ", this.newPlanList);
    if (cycle?.toLowerCase() === MONTHLY || cycle?.toLowerCase() === YEARLY) {
      this.plansBasedOnCycle = this.newPlanList.filter((plan: any) => {
        const prices = plan?.availability?.prices?.[this.region.toUpperCase()];
        if (prices) {
          //
          // It looks like the issue is due to the structure of the prices object in the plan.
          // Specifically, for the CA region, it does not have the expected array structure but an object structure instead.
          // This is why prices[0]?.cycle?.toLowerCase() is returning undefined
          //

          if (Array.isArray(prices)) {
            // Handle array structure
            return prices.some((price) => price.cycle?.toLowerCase() === cycle);
          } else {
            // Handle object structure
            return prices.cycle?.toLowerCase() === cycle;
          }
        }
        return false;
      });
    }
    this.cdr.detectChanges(); // Ensure change detection runs
  }

  // get New Plans here --------------------------->
  getNewPlans() {
    this.isLoading$.next(true);
    if (!this.region) {
      this.region = localStorage.getItem("selectedCountry");
    }
    let queryParams = {
      region: this.region,
    };
    this.service
      .Post(`plans/getuserplans`, this.selectedDevices, queryParams)
      .subscribe(
        (data: any) => {
          const selectDevicesMOdels = this.selectedDevices.map(
            (device: any) => device.model
          );
          this.newPlanList = data.filter((plan: any) => {
            return (
              plan?.status === "available" &&
              plan?.availability?.regions?.includes(
                this.region?.toUpperCase()
              ) &&
              selectDevicesMOdels.every((element: any) =>
                plan?.details?.association.device_models.includes(element)
              ) &&
              (plan?.details.association.channels === 1 ||
                plan?.details.association.channels >=
                  this.selectedDevices.length)
            );
          });
          this.planDetails = data[0]?.Plan_details;
          this.setActiveTab(MONTHLY);
          this.isLoading$.next(false);
        },
        (error: any) => {
          this.isLoading$.next(false);
        }
      );
  }

  getMaxChannels = (channels: any) => {
    if (Array.isArray(channels)) {
      return Math.max(...channels);
    } else {
      return channels;
    }
  };

  onClickCheckout() {
    this.isLoading$.next(true);
    const idAndModels = this.selectedDevices?.map((device: any) => ({
      id: device.id,
      model: device.model,
    }));
    const maxChannels = this.getMaxChannels(
      this.activePlan?.details?.association?.channels
    );
    const data = {
      user: this.user,
      devices: idAndModels,
      selling_plan_id: this.activePlan?.selling_plan_id,
      shopify_variant_id: this.activePlan?.shopify_variant_id,
      plan_id: this.activePlan?.id,
      regions: this.region?.toUpperCase(),
      channels: maxChannels,
    };

    this.notificationService.setCreateNewPlanDevice(this.activePlan?.id);
    this.service.Post(`payment/create-checkout-session`, data).subscribe(
      (data: any) => {
        this.checkoutUrl = data?.cartData?.cartCreate?.cart?.checkoutUrl;
        //window.location.href = this.checkoutUrl;
        window.open(this.checkoutUrl, "_blank", "noopener,noreferrer");
        this.isLoading$.next(true);

        setTimeout(() => {
          this.isLoading$.next(false);
          this.notificationService.setStatusForCheckSuccess(true);
          //this.router.navigate(['/success']);
        });
      },
      (error: any) => {
        this.toastr.error(`Unable to create checkout!`);
        this.router.navigate(["/home"]);
      }
    );
  }

  protected readonly Math = Math;
}
